import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Share2, Copy, MessageCircle } from "lucide-react";
import { useT } from "@/lib/i18n";

const reduced = () => window.matchMedia("(prefers-reduced-motion: reduce)").matches;

export function HaloCard({ children, className = "", testid, ...rest }) {
  const [burst, setBurst] = useState(0);
  const inside = useRef(false);
  const enter = () => { if (!inside.current && !reduced()) setBurst(Date.now()); inside.current = true; };
  const leave = () => { inside.current = false; };
  return (
    <div className={`halo ${className}`} onMouseEnter={enter} onMouseLeave={leave} onFocus={enter} onBlur={leave} data-testid={testid} {...rest}>
      {children}
      {burst > 0 && (
        <span className="bubbles" aria-hidden="true" key={burst}>
          {Array.from({ length: 10 }).map((_, i) => (
            <span key={i} className="bubble" style={{ left: `${8 + i * 9}%`, animationDelay: `${(i % 5) * 0.12}s`, width: 4 + (i % 3) * 2, height: 4 + (i % 3) * 2 }} />
          ))}
        </span>
      )}
    </div>
  );
}

export const Eyebrow = ({ children }) => <p className="eyebrow mb-4">{children}</p>;

export function PageHead({ eyebrow, title, lead, dark = false }) {
  return (
    <header className={`${dark ? "dark-zone bg-[var(--forest)] text-[var(--ivory)]" : "bg-gradient-to-b from-[var(--ivory)] to-[var(--lavender)]"} pt-16 pb-12 md:pt-24 md:pb-16 relative overflow-hidden`}>
      <img src="/assets/mandala.svg" alt="" aria-hidden="true" className="mandala-spin absolute -right-32 -top-32 w-[420px] opacity-[0.07] pointer-events-none" />
      <div className="wrap relative reveal">
        {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
        <h1 className="max-w-[18ch]">{title}</h1>
        {lead && <p className={`lead mt-6 ${dark ? "!text-[var(--line)]" : ""}`}>{lead}</p>}
      </div>
    </header>
  );
}

export function ShareBar({ path, text }) {
  const { tl, locale } = useT();
  const [s1, s2, s3, copied, share] = tl("sharing");
  const [msg, setMsg] = useState("");
  const url = `${window.location.origin}/${locale}${path}`;
  const enc = encodeURIComponent;
  const copy = async () => { try { await navigator.clipboard.writeText(url); setMsg(copied); } catch { setMsg(url); } };
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="share-bar">
      <span className="text-sm font-semibold mr-1">{s1}</span>
      {navigator.share && <button className="chip" onClick={() => navigator.share({ title: "Ayahuasca Angel", text, url }).catch(() => {})} data-testid="share-native"><Share2 size={15} className="mr-1" />{share}</button>}
      <a className="chip" href={`https://wa.me/?text=${enc(`${text} ${url}`)}`} target="_blank" rel="noreferrer" data-testid="share-whatsapp"><MessageCircle size={15} className="mr-1" />{s2}</a>
      <a className="chip" href={`https://www.facebook.com/sharer/sharer.php?u=${enc(url)}`} target="_blank" rel="noreferrer" data-testid="share-facebook">Facebook</a>
      <a className="chip" href={`https://x.com/intent/post?text=${enc(text)}&url=${enc(url)}`} target="_blank" rel="noreferrer" data-testid="share-x">X</a>
      <button className="chip" onClick={copy} data-testid="share-copy"><Copy size={15} className="mr-1" />{s3}</button>
      <span aria-live="polite" className="text-sm text-[var(--muted)]" data-testid="share-status">{msg}</span>
    </div>
  );
}

export const CtaLink = ({ to, children, variant = "gold", testid, className = "" }) => (
  <Link to={to} className={`btn btn-${variant} ${className}`} data-testid={testid}>{children}</Link>
);

export const Notice = ({ children, testid }) => <p className="notice" data-testid={testid}>{children}</p>;
